/**
 * 性能监控工具
 * 用于监控Core Web Vitals和用户交互性能
 */

class PerformanceMonitor {
    constructor() {
        this.metrics = new Map();
        this.observers = [];
        this.isEnabled = true;

        // 初始化性能监控
        this.init();
    }

    /**
     * 初始化性能监控
     */
    init() {
        if (typeof window === 'undefined') return;

        // 监控 Core Web Vitals
        this.observeCoreWebVitals();

        // 监控用户交互
        this.observeUserInteractions();

        // 监控资源加载
        this.observeResourceLoading();
    }

    /**
     * 标记用户交互
     * @param {string} action - 交互动作名称
     * @param {Object} metadata - 额外的元数据
     */
    markUserInteraction(action, metadata = {}) {
        if (!this.isEnabled) return;

        const timestamp = performance.now();
        const metric = {
            type: 'user_interaction',
            action,
            timestamp,
            metadata,
            url: window.location.pathname
        };

        this.metrics.set(`interaction_${action}_${timestamp}`, metric);
        console.log(`[Performance] User Interaction: ${action}`, metadata);
    }

    /**
     * 测量chunk加载时间
     * @param {string} chunkName - chunk名称
     * @param {number} startTime - 开始时间
     */
    measureChunkLoad(chunkName, startTime) {
        if (!this.isEnabled) return;

        const loadTime = performance.now() - startTime;
        const metric = {
            type: 'chunk_load',
            chunkName,
            loadTime,
            timestamp: performance.now()
        };

        this.metrics.set(`chunk_${chunkName}`, metric);
        console.log(`[Performance] Chunk Load: ${chunkName} - ${loadTime.toFixed(2)}ms`);
    }

    /**
     * 监控 Core Web Vitals
     */
    observeCoreWebVitals() {
        if (!('PerformanceObserver' in window)) return;

        // LCP (Largest Contentful Paint)
        try {
            const lcpObserver = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                const lastEntry = entries[entries.length - 1];
                this.recordMetric('LCP', lastEntry.startTime, {
                    element: lastEntry.element?.tagName
                });
            });
            lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
            this.observers.push(lcpObserver);
        } catch (e) {
            console.warn('[Performance] LCP observer not supported', e);
        }

        // FID (First Input Delay)
        try {
            const fidObserver = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                entries.forEach((entry) => {
                    this.recordMetric('FID', entry.processingStart - entry.startTime, {
                        eventType: entry.name
                    });
                });
            });
            fidObserver.observe({ entryTypes: ['first-input'] });
            this.observers.push(fidObserver);
        } catch (e) {
            console.warn('[Performance] FID observer not supported', e);
        }

        // CLS (Cumulative Layout Shift)
        try {
            let clsValue = 0;
            const clsObserver = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                entries.forEach((entry) => {
                    if (!entry.hadRecentInput) {
                        clsValue += entry.value;
                        this.recordMetric('CLS', clsValue);
                    }
                });
            });
            clsObserver.observe({ entryTypes: ['layout-shift'] });
            this.observers.push(clsObserver);
        } catch (e) {
            console.warn('[Performance] CLS observer not supported', e);
        }
    }

    /**
     * 监控用户交互
     */
    observeUserInteractions() {
        // 监控路由变化
        let currentPath = window.location.pathname;
        const checkPathChange = () => {
            if (window.location.pathname !== currentPath) {
                this.markUserInteraction('route_change', {
                    from: currentPath,
                    to: window.location.pathname
                });
                currentPath = window.location.pathname;
            }
        };

        // 定期检查路由变化
        setInterval(checkPathChange, 100);
    }

    /**
     * 监控资源加载
     */
    observeResourceLoading() {
        if (!('PerformanceObserver' in window)) return;

        try {
            const resourceObserver = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                entries.forEach((entry) => {
                    if (entry.initiatorType === 'script' && entry.name.includes('chunk')) {
                        this.recordMetric('resource_load', entry.duration, {
                            resource: entry.name,
                            size: entry.transferSize,
                            type: entry.initiatorType
                        });
                    }
                });
            });
            resourceObserver.observe({ entryTypes: ['resource'] });
            this.observers.push(resourceObserver);
        } catch (e) {
            console.warn('[Performance] Resource observer not supported', e);
        }
    }

    /**
     * 记录性能指标
     * @param {string} name - 指标名称
     * @param {number} value - 指标值
     * @param {Object} metadata - 额外数据
     */
    recordMetric(name, value, metadata = {}) {
        const metric = {
            name,
            value,
            timestamp: performance.now(),
            metadata,
            url: window.location.pathname
        };

        this.metrics.set(`${name}_${Date.now()}`, metric);

        // 根据阈值判断性能等级
        let level = 'good';
        if (name === 'LCP' && value > 2500) level = value > 4000 ? 'poor' : 'needs-improvement';
        if (name === 'FID' && value > 100) level = value > 300 ? 'poor' : 'needs-improvement';
        if (name === 'CLS' && value > 0.1) level = value > 0.25 ? 'poor' : 'needs-improvement';

        console.log(`[Performance] ${name}: ${value.toFixed(2)}ms [${level}]`, metadata);
    }

    /**
     * 获取所有性能指标
     */
    getMetrics() {
        return Array.from(this.metrics.values());
    }

    /**
     * 获取指定类型的指标
     * @param {string} type - 指标类型
     */
    getMetricsByType(type) {
        return this.getMetrics().filter(metric =>
            metric.name === type || metric.type === type
        );
    }

    /**
     * 清空所有指标
     */
    clearMetrics() {
        this.metrics.clear();
    }

    /**
     * 禁用性能监控
     */
    disable() {
        this.isEnabled = false;
        this.observers.forEach(observer => observer.disconnect());
        this.observers = [];
    }

    /**
     * 启用性能监控
     */
    enable() {
        this.isEnabled = true;
        this.init();
    }

    /**
     * 生成性能报告
     */
    generateReport() {
        const metrics = this.getMetrics();
        const report = {
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
            coreWebVitals: {
                LCP: this.getMetricsByType('LCP').slice(-1)[0]?.value || null,
                FID: this.getMetricsByType('FID').slice(-1)[0]?.value || null,
                CLS: this.getMetricsByType('CLS').slice(-1)[0]?.value || null
            },
            userInteractions: this.getMetricsByType('user_interaction'),
            chunkLoads: this.getMetricsByType('chunk_load'),
            resourceLoads: this.getMetricsByType('resource_load'),
            totalMetrics: metrics.length
        };

        console.log('[Performance] Report Generated:', report);
        return report;
    }
}

// 创建全局实例
const performanceMonitor = new PerformanceMonitor();

export default performanceMonitor; 